import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AppMenuitem } from './app.menuitem';
import { AuthService } from '../../core/auth/services/auth.service';
import { ProviderAuthService } from '../../core/auth/services/provider-auth.service';

@Component({
    selector: 'app-menu',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule],
    template: `<ul class="layout-menu">
        <ng-container *ngFor="let item of model; let i = index">
            <li app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="true"></li>
            <li *ngIf="item.separator" class="menu-separator"></li>
        </ng-container>
    </ul> `
})
export class AppMenu implements OnInit {
    model: MenuItem[] = [];
    private authService = inject(AuthService);
    private providerAuthService = inject(ProviderAuthService);

    ngOnInit() {
        const hasAdminRole = this.authService.hasAnyRole(['super_admin', 'Administrador']);
        const hasAnalistaRole = this.authService.hasAnyRole(['Analista', 'analista']);
        const hasVendedorRole = this.authService.hasAnyRole(['Vendedor', 'vendedor']);
        const hasLogisticaRole = this.authService.hasAnyRole(['Logistica', 'logistica']);
        const hasClienteRole = this.authService.hasAnyRole(['Cliente', 'cliente']);
        const hasProveedorRole = this.authService.hasAnyRole(['Proveedor', 'proveedor']);
        const hasContabilidadRole = this.authService.hasAnyRole(['Contabilidad', 'contabilidad']);
        const hasGerenteComercialRole = this.authService.hasAnyRole(['Gerente Comercial']);
        const hasAdministradorDeEmpresaRole = this.authService.hasAnyRole(['Administrador de Empresa']);
        const hasGerenteRole = this.authService.hasAnyRole(['Gerente']);
        const hasGestorMaquinariaRole = this.authService.hasAnyRole(['Gestor de Maquinaria']);
        const hasAlmacenistaRole = this.authService.hasAnyRole(['Almacenista']);
        const hasContadorRole = this.authService.hasAnyRole(['Contador']);

        // Caso 1: Sesión de Proveedor (Detección unificada)
        if (this.providerAuthService.isProvider() || hasProveedorRole) {
            const authUser = typeof this.authService.currentUser === 'function' ? this.authService.currentUser() : (this.authService as any)?.currentUser;
            const provUser = typeof this.providerAuthService.currentUser === 'function' ? this.providerAuthService.currentUser() : (this.providerAuthService as any)?.currentUser;
            const providerRoles: string[] = [
                ...(Array.isArray(authUser?.roles) ? authUser.roles : []),
                ...(Array.isArray(provUser?.roles) ? provUser.roles : [])
            ];

            const hasVendedorProv = providerRoles.includes('Vendedor Proveedor') || this.authService.hasRole('Vendedor Proveedor') || this.providerAuthService.hasRole('Vendedor Proveedor');
            const hasLogisticaProv = providerRoles.includes('Logistica Proveedor') || this.authService.hasRole('Logistica Proveedor') || this.providerAuthService.hasRole('Logistica Proveedor');
            const hasFacturadorProv = providerRoles.includes('Facturador Proveedor') || this.authService.hasRole('Facturador Proveedor') || this.providerAuthService.hasRole('Facturador Proveedor');
            const hasAdminEmpresaProv = providerRoles.includes('Administrador de Empresa') || hasAdministradorDeEmpresaRole || this.providerAuthService.hasRole('Administrador de Empresa');

            const hasAnyFunctionalRole = hasVendedorProv || hasLogisticaProv || hasFacturadorProv || hasAdminEmpresaProv;

            const providerMenuItems: MenuItem[] = [
                {
                    label: 'Inicio',
                    icon: 'pi pi-fw pi-home',
                    routerLink: ['/provider'],
                    routerLinkActiveOptions: { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' }
                }
            ];

            const showOpportunities = hasAdminEmpresaProv || hasVendedorProv || !hasAnyFunctionalRole;
            const showOrders = hasAdminEmpresaProv || hasVendedorProv || hasLogisticaProv || !hasAnyFunctionalRole;
            const showFacturacion = hasAdminEmpresaProv || hasFacturadorProv;

            if (showOpportunities) {
                providerMenuItems.push({
                    label: 'Oportunidades de Costeo',
                    icon: 'pi pi-fw pi-bolt',
                    routerLink: ['/provider/opportunities']
                });
            }

            if (showOrders) {
                providerMenuItems.push({
                    label: 'Mis Órdenes de Compra',
                    icon: 'pi pi-fw pi-shopping-bag',
                    routerLink: ['/provider/ordenes-compra']
                });
            }

            if (showFacturacion) {
                providerMenuItems.push({
                    label: 'Pagos y Facturación',
                    icon: 'pi pi-fw pi-receipt',
                    routerLink: ['/provider/facturacion']
                });
            }

            this.model = [
                {
                    label: 'Portal de Proveedores',
                    items: providerMenuItems
                },
                {
                    label: 'Perfil',
                    items: [{ label: 'Cerrar Sesión', icon: 'pi pi-fw pi-sign-out', command: () => (hasProveedorRole ? this.authService.logout().subscribe() : this.providerAuthService.logout()) }]
                }
            ];
            return;
        }

        // Caso 2: Sesión Interna (Asesores, Analistas, Admin)

        // Caso especial: Logistica ve Órdenes de Trabajo y Órdenes de Compra
        if (hasLogisticaRole && !hasAdminRole) {
            this.model = [
                {
                    label: 'Operaciones',
                    items: [
                        { label: 'Órdenes de Compra', icon: 'pi pi-fw pi-shopping-bag', routerLink: ['/app/ordenes-compra'] },
                        { label: 'Órdenes de Trabajo', icon: 'pi pi-fw pi-briefcase', routerLink: ['/app/ordenes-trabajo'] }
                    ]
                },
                {
                    label: 'Perfil',
                    items: [{ label: 'Cerrar Sesión', icon: 'pi pi-fw pi-sign-out', command: () => this.authService.logout() }]
                }
            ];
            return;
        }

        // Caso especial: Gerente Comercial ve solo Órdenes de Compra (aprobación gerencial)
        if (hasGerenteComercialRole && !hasAdminRole) {
            this.model = [
                {
                    label: 'Gerencia',
                    items: [{ label: 'Órdenes de Compra', icon: 'pi pi-fw pi-shopping-bag', routerLink: ['/app/ordenes-compra'] }]
                },
                {
                    label: 'Perfil',
                    items: [{ label: 'Cerrar Sesión', icon: 'pi pi-fw pi-sign-out', command: () => this.authService.logout() }]
                }
            ];
            return;
        }

        // Caso especial: Contabilidad ve solo la bandeja de Facturación
        if (hasContabilidadRole && !hasAdminRole) {
            this.model = [
                {
                    label: 'Facturación',
                    items: [
                        { label: 'Órdenes por Facturar', icon: 'pi pi-fw pi-receipt', routerLink: ['/app/ordenes-trabajo/facturacion'] },
                        { label: 'Órdenes de Compra', icon: 'pi pi-fw pi-shopping-bag', routerLink: ['/app/ordenes-compra'] }
                    ]
                },
                {
                    label: 'Perfil',
                    items: [{ label: 'Cerrar Sesión', icon: 'pi pi-fw pi-sign-out', command: () => this.authService.logout() }]
                }
            ];
            return;
        }

        // Caso especial: Analista (y no Admin) solo ve lo solicitado
        if (hasAnalistaRole && !hasAdminRole) {
            this.model = [
                {
                    label: 'Comercial',
                    items: [{ label: 'Análisis', icon: 'pi pi-fw pi-shopping-cart', routerLink: ['/app/pedidos'] }]
                },
                {
                    label: 'Catálogo de Productos',
                    items: [
                        { label: 'Máquinas', icon: 'pi pi-fw pi-cog', routerLink: ['/app/maquinas'] },
                        { label: 'Sistemas', icon: 'pi pi-fw pi-wrench', routerLink: ['/app/sistemas'] },
                        { label: 'Listas', icon: 'pi pi-fw pi-list-check', routerLink: ['/app/listas'] },
                        { label: 'Artículos', icon: 'pi pi-fw pi-box', routerLink: ['/app/articulos'] },
                        { label: 'Referencias', icon: 'pi pi-fw pi-hashtag', routerLink: ['/app/referencias'] }
                    ]
                }
            ];
            return;
        }

        // Caso especial: Cliente (empresa externa, Fase 0 #192, Issue #230)
        if (hasClienteRole && !hasAdminRole && !hasVendedorRole && !hasAnalistaRole && !hasLogisticaRole && !hasContabilidadRole) {
            // Caso A: Gestor de Maquinaria puro (Fase 9 / Issue #230)
            // Exactamente 4 secciones funcionales: Principal, Operaciones, Gestión de Compras y Flota (+ Perfil).
            if (hasGestorMaquinariaRole && !hasAdministradorDeEmpresaRole && !hasGerenteRole && !hasAlmacenistaRole) {
                this.model = [
                    {
                        label: 'Principal',
                        items: [{ label: 'Dashboard', icon: 'pi pi-fw pi-home', routerLink: ['/app'], routerLinkActiveOptions: { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' } }]
                    },
                    {
                        label: 'Operaciones',
                        items: [{ label: 'Mis Pedidos', icon: 'pi pi-fw pi-shopping-cart', routerLink: ['/app/pedidos'] }]
                    },
                    {
                        label: 'Gestión de Compras',
                        items: [
                            { label: 'Cotizaciones', icon: 'pi pi-fw pi-file', routerLink: ['/app/cotizaciones'] },
                            { label: 'Órdenes de Trabajo', icon: 'pi pi-fw pi-briefcase', routerLink: ['/app/ordenes-trabajo'] }
                        ]
                    },
                    {
                        label: 'Flota',
                        items: [{ label: 'Máquinas', icon: 'pi pi-fw pi-cog', routerLink: ['/app/maquinas'] }]
                    },
                    {
                        label: 'Perfil',
                        items: [{ label: 'Cerrar Sesión', icon: 'pi pi-fw pi-sign-out', command: () => this.authService.logout() }]
                    }
                ];
                return;
            }

            // Caso B: Administrador de Empresa (Fase 2 #197, Issue #230)
            // Ve las 4 secciones del Gestor de Maquinaria más lo propio de su rol:
            // - Gestión de Compras: Cotizaciones, Órdenes de Trabajo y Órdenes de Compra
            // - Operaciones / Almacén: Mis Pedidos, Inventario de Transición y Remisiones de Salida
            // - Flota: Máquinas
            // - Empresa: Datos de la Empresa y Mi Equipo
            if (hasAdministradorDeEmpresaRole) {
                this.model = [
                    {
                        label: 'Principal',
                        items: [{ label: 'Dashboard', icon: 'pi pi-fw pi-home', routerLink: ['/app'], routerLinkActiveOptions: { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' } }]
                    },
                    {
                        label: 'Operaciones',
                        items: [
                            { label: 'Mis Pedidos', icon: 'pi pi-fw pi-shopping-cart', routerLink: ['/app/pedidos'] },
                            { label: 'Inventario de Transición', icon: 'pi pi-fw pi-warehouse', routerLink: ['/app/inventario-transicion'] },
                            { label: 'Remisiones de Salida', icon: 'pi pi-fw pi-file-export', routerLink: ['/app/remisiones-salida'] }
                        ]
                    },
                    {
                        label: 'Gestión de Compras',
                        items: [
                            { label: 'Cotizaciones', icon: 'pi pi-fw pi-file', routerLink: ['/app/cotizaciones'] },
                            { label: 'Órdenes de Trabajo', icon: 'pi pi-fw pi-briefcase', routerLink: ['/app/ordenes-trabajo'] },
                            { label: 'Órdenes de Compra', icon: 'pi pi-fw pi-shopping-bag', routerLink: ['/app/ordenes-compra'] }
                        ]
                    },
                    {
                        label: 'Flota',
                        items: [{ label: 'Máquinas', icon: 'pi pi-fw pi-cog', routerLink: ['/app/maquinas'] }]
                    },
                    {
                        label: 'Empresa',
                        items: [
                            { label: 'Datos de la Empresa', icon: 'pi pi-fw pi-building', routerLink: ['/app/mi-empresa'] },
                            { label: 'Mi Equipo', icon: 'pi pi-fw pi-users', routerLink: ['/app/equipo'] }
                        ]
                    },
                    {
                        label: 'Perfil',
                        items: [{ label: 'Cerrar Sesión', icon: 'pi pi-fw pi-sign-out', command: () => this.authService.logout() }]
                    }
                ];
                return;
            }

            // Caso C: Contador de empresa cliente (Fase 6B / Issue #240)
            // Menú exclusivo: Principal (Dashboard) y Órdenes de Compra (+ Perfil).
            if (hasContadorRole && !hasAdministradorDeEmpresaRole && !hasGerenteRole && !hasGestorMaquinariaRole && !hasAlmacenistaRole) {
                this.model = [
                    {
                        label: 'Principal',
                        items: [{ label: 'Dashboard', icon: 'pi pi-fw pi-home', routerLink: ['/app'], routerLinkActiveOptions: { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' } }]
                    },
                    {
                        label: 'Operaciones',
                        items: [{ label: 'Órdenes de Compra', icon: 'pi pi-fw pi-shopping-bag', routerLink: ['/app/ordenes-compra'] }]
                    },
                    {
                        label: 'Perfil',
                        items: [{ label: 'Cerrar Sesión', icon: 'pi pi-fw pi-sign-out', command: () => this.authService.logout() }]
                    }
                ];
                return;
            }

            // Caso D: Gerente y Almacenista (u otros roles cliente operativos preexistentes)
            // Conservan su menú actual intacto bajo 'Mi Empresa'.
            const esRolOperativo = (hasGerenteRole || hasAlmacenistaRole) && !hasGestorMaquinariaRole;
            const miEmpresaItems = esRolOperativo
                ? []
                : [
                      { label: 'Gestionar Maquinaria', icon: 'pi pi-fw pi-shopping-cart', routerLink: ['/app/pedidos'] },
                      { label: 'Cotizaciones', icon: 'pi pi-fw pi-file', routerLink: ['/app/cotizaciones'] }
                  ];

            if (hasGerenteRole || hasAlmacenistaRole) {
                miEmpresaItems.push({ label: 'Órdenes de Compra', icon: 'pi pi-fw pi-shopping-bag', routerLink: ['/app/ordenes-compra'] });
            }

            if (hasAlmacenistaRole) {
                miEmpresaItems.push({ label: 'Inventario de Transición', icon: 'pi pi-fw pi-warehouse', routerLink: ['/app/inventario-transicion'] });
                miEmpresaItems.push({ label: 'Remisiones de Salida', icon: 'pi pi-fw pi-file-export', routerLink: ['/app/remisiones-salida'] });
            }

            this.model = [
                {
                    label: 'Principal',
                    items: [{ label: 'Dashboard', icon: 'pi pi-fw pi-home', routerLink: ['/app'], routerLinkActiveOptions: { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' } }]
                },
                {
                    label: 'Mi Empresa',
                    items: miEmpresaItems
                },
                {
                    label: 'Perfil',
                    items: [{ label: 'Cerrar Sesión', icon: 'pi pi-fw pi-sign-out', command: () => this.authService.logout() }]
                }
            ];
            return;
        }

        this.model = [
            {
                label: 'Principal',
                items: [{ label: 'Dashboard', icon: 'pi pi-fw pi-home', routerLink: ['/app'], routerLinkActiveOptions: { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' } }]
            }
        ];

        if (hasAdminRole) {
            this.model.push({
                label: 'Administración',
                items: [
                    { label: 'Gestión de Usuarios', icon: 'pi pi-fw pi-id-card', routerLink: ['/app/usuarios'] },
                    { label: 'Gestión de Países', icon: 'pi pi-fw pi-globe', routerLink: ['/app/countries'] }
                ]
            });
        }

        this.model.push(
            {
                label: 'Comercial',
                items: [{ label: 'Pedidos', icon: 'pi pi-fw pi-shopping-cart', routerLink: ['/app/pedidos'] }]
            },
            {
                label: 'Documentos',
                items: [
                    { label: 'Cotizaciones', icon: 'pi pi-fw pi-file', routerLink: ['/app/cotizaciones'] },
                    { label: 'Órdenes de Compra', icon: 'pi pi-fw pi-shopping-bag', routerLink: ['/app/ordenes-compra'] },
                    { label: 'Órdenes de Trabajo', icon: 'pi pi-fw pi-briefcase', routerLink: ['/app/ordenes-trabajo'] },
                    { label: 'Facturación', icon: 'pi pi-fw pi-receipt', routerLink: ['/app/ordenes-trabajo/facturacion'] }
                ]
            },
            {
                label: 'Catálogo de Productos',
                items: [
                    { label: 'Máquinas', icon: 'pi pi-fw pi-cog', routerLink: ['/app/maquinas'] },
                    { label: 'Sistemas', icon: 'pi pi-fw pi-wrench', routerLink: ['/app/sistemas'] },
                    { label: 'Listas', icon: 'pi pi-fw pi-list-check', routerLink: ['/app/listas'] },
                    { label: 'Artículos', icon: 'pi pi-fw pi-box', routerLink: ['/app/articulos'] },
                    { label: 'Referencias', icon: 'pi pi-fw pi-hashtag', routerLink: ['/app/referencias'] }
                ]
            }
        );

        // CRM & Terceros (Condicional para Empresas)
        const crmItems = [{ label: 'Terceros', icon: 'pi pi-fw pi-users', routerLink: ['/app/terceros'] }];
        if (!hasVendedorRole || hasAdminRole) {
            crmItems.push({ label: 'Empresas', icon: 'pi pi-fw pi-building', routerLink: ['/app/empresas'] });
        }
        // Contactos y Direcciones: ocultos hasta tener funcionalidad completa

        this.model.push({
            label: 'CRM & Terceros',
            items: crmItems
        });

        // Configuración & Logística (Condicional para TRM)
        const configItems = [
            { label: 'Transportadoras', icon: 'pi pi-fw pi-truck', routerLink: ['/app/transportadoras'] },
            { label: 'Categorías (ERP)', icon: 'pi pi-fw pi-tags', routerLink: ['/app/categorias'] }
        ];
        if (!hasVendedorRole || hasAdminRole) {
            configItems.push({ label: 'Tasa de Cambio (TRM)', icon: 'pi pi-fw pi-dollar', routerLink: ['/app/trms'] });
        }

        this.model.push({
            label: 'Configuración & Logística',
            items: configItems
        });

        if (hasAdminRole) {
            this.model.push({
                label: 'Landing',
                items: [
                    { label: 'Categorías Landing', icon: 'pi pi-fw pi-list', routerLink: ['/app/gestion-landing'], routerLinkActiveOptions: { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' } },
                    { label: 'Tipos de Máquina', icon: 'pi pi-fw pi-cog', routerLink: ['/app/gestion-landing/machine-types'] },
                    { label: 'Clientes Interesados', icon: 'pi pi-fw pi-users', routerLink: ['/app/gestion-landing/contact-leads'] }
                ]
            });
        }
    }
}
